// Bilan « Ta semaine » (7 derniers jours) : agrège adhérence, tendance et
// apport moyen en un verdict + un conseil, sans nouveau calcul.

import { adherence } from './calorie-analytics';
import { filterByPeriod } from './period';
import { round } from './statistics';
import type { Consumption, TrainingPhase, WeightEntry } from './types';
import { currentWeight } from './weight-analytics';

export interface WeeklyDigest {
  /** Jours (sur 7) avec au moins une saisie de calories. */
  daysLogged: number;
  adherencePct: number;
  avgKcal: number;
  /** kg, négatif = perte. */
  weightDeltaKg: number;
  advice: string;
}

export function weeklyDigest(
  consumptions: readonly Consumption[],
  weights: readonly WeightEntry[],
  threshold: number,
  phase: TrainingPhase = 'cut',
): WeeklyDigest {
  const weekConsumptions = filterByPeriod(consumptions, (c) => c.datetime, '7d');
  const weekWeights = filterByPeriod(weights, (w) => w.date, '7d');
  const adh = adherence(weekConsumptions, threshold, phase);

  const weightDeltaKg =
    weekWeights.length >= 2
      ? round(currentWeight(weekWeights, 1) - weekWeights[0]!.weightKg, 2)
      : 0;

  return {
    daysLogged: adh.daysLogged,
    adherencePct: adh.adherencePct,
    avgKcal: adh.avgKcal,
    weightDeltaKg,
    advice: buildAdvice(adh.daysLogged, adh.adherencePct, weightDeltaKg, weekWeights.length, phase),
  };
}

// Un seul conseil, le plus pertinent, pour rester actionnable — orienté par la phase.
function buildAdvice(
  daysLogged: number,
  adherencePct: number,
  weightDeltaKg: number,
  weightCount: number,
  phase: TrainingPhase,
): string {
  if (daysLogged < 3) {
    return 'Peu de jours saisis cette semaine : logue tes repas plus régulièrement pour un suivi fiable.';
  }
  if (weightCount < 2) {
    return 'Pèse-toi au moins deux fois par semaine pour suivre ta progression réelle.';
  }
  if (phase === 'bulk') {
    if (weightDeltaKg > 0) {
      return 'Prise de masse en cours : poids en hausse, continue le surplus régulier 💪';
    }
    return 'Poids stable ou en baisse : augmente légèrement les portions pour soutenir la prise.';
  }
  if (phase === 'recovery') {
    if (Math.abs(weightDeltaKg) <= 0.3) {
      return 'Poids stable : bon équilibre énergétique pour la récupération.';
    }
    return 'En récupération, vise l’équilibre : évite les gros déficits comme les gros surplus.';
  }
  // Sèche (défaut).
  if (adherencePct >= 80 && weightDeltaKg <= 0) {
    return 'Semaine solide : adhérence élevée et poids en baisse. Continue sur cette lancée 💪';
  }
  if (adherencePct < 50) {
    return 'Adhérence faible : vise de petits dépassements plutôt que de gros écarts pour tenir le seuil.';
  }
  if (weightDeltaKg > 0) {
    return 'Poids en hausse cette semaine : vérifie les portions et l’exactitude des saisies.';
  }
  return 'Semaine correcte : encore un peu de régularité pour accélérer la progression.';
}
